import { useAtom } from '@reatom/npm-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router'
import styled from 'styled-components'

import { pipe, Url } from '@blade/std-lib'
import { platformUtilAtom } from '@root/shared/core'
import { useTranslation } from '@root/shared/localization'

import type React from 'react'

const BROWSER_SERVER_PORT = 12_582

declare global {
  interface Window {
    __DELUGE_LOCAL_TOKEN__?: string
  }
}

const isMac =
  typeof navigator !== 'undefined' && /Mac/.test(navigator.userAgent)
const isWindows =
  typeof navigator !== 'undefined' && /Win/.test(navigator.userAgent)

function useHistoryNav() {
  const location = useLocation()
  const navigationType = useNavigationType()
  const [maxIdx, setMaxIdx] = useState(0)
  const [currentIdx, setCurrentIdx] = useState(0)

  useEffect(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    setCurrentIdx(idx)
    setMaxIdx((prev) => (navigationType === 'PUSH' ? idx : Math.max(prev, idx)))
  }, [location.key, navigationType])

  return {
    canBack: currentIdx > 0,
    canForward: currentIdx < maxIdx,
  }
}

export const AppTitleBar: React.FC = () => {
  const [platformUtil] = useAtom(platformUtilAtom)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { canBack, canForward } = useHistoryNav()

  const handleBack = () => {
    navigate(-1)
  }
  const handleForward = () => {
    navigate(1)
  }
  const handleReload = () => {
    window.location.reload()
  }
  const handleOpenInBrowser = () => {
    const token =
      window.__DELUGE_LOCAL_TOKEN__ ??
      sessionStorage.getItem('__DELUGE_LOCAL_TOKEN__')
    if (!token) {
      return
    }
    const openLink = pipe(
      new URL(window.location.href),
      Url.setProtocol('http'),
      Url.setHostname('deluge.localhost'),
      Url.setPort(String(BROWSER_SERVER_PORT)),
      Url.mutate((x) => x.searchParams.set('token', token)),
    )

    platformUtil.openLink(openLink)
  }

  const handleMinimize = () => {
    void platformUtil.window.minimize()
  }
  const handleToggleMaximize = () => {
    void platformUtil.window.toggleMaximize()
  }
  const handleClose = () => {
    void platformUtil.window.close()
  }

  return (
    <Root data-tauri-drag-region>
      {isMac ? <MacTrafficLightsSpacer /> : undefined}
      <LeftActions data-tauri-drag-region>
        <IconButton
          type="button"
          onClick={handleBack}
          disabled={!canBack}
          title={t('common.back')}
          aria-label={t('common.back')}
        >
          <BackIcon />
        </IconButton>
        <IconButton
          type="button"
          onClick={handleForward}
          disabled={!canForward}
          title={t('app-titlebar.forward')}
          aria-label={t('app-titlebar.forward')}
        >
          <ForwardIcon />
        </IconButton>
        <IconButton
          type="button"
          onClick={handleReload}
          title={t('common.reload')}
          aria-label={t('common.reload')}
        >
          <ReloadIcon />
        </IconButton>
        <Divider />
        <OpenInBrowserButton type="button" onClick={handleOpenInBrowser}>
          <BrowserWindowIcon />
          <span>{t('app-titlebar.open-in-browser')}</span>
        </OpenInBrowserButton>
      </LeftActions>
      <DragRegion data-tauri-drag-region />
      {isWindows ? (
        <RightActions>
          <IconButton
            type="button"
            onClick={handleMinimize}
            title={t('app-titlebar.minimize')}
            aria-label={t('app-titlebar.minimize')}
          >
            <MinimizeIcon />
          </IconButton>
          <IconButton
            type="button"
            onClick={handleToggleMaximize}
            title={t('app-titlebar.maximize')}
            aria-label={t('app-titlebar.maximize')}
          >
            <MaximizeIcon />
          </IconButton>
          <CloseButton
            type="button"
            onClick={handleClose}
            title={t('common.close')}
            aria-label={t('common.close')}
          >
            <CloseIcon />
          </CloseButton>
        </RightActions>
      ) : undefined}
    </Root>
  )
}

const BackIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="20" y1="12" x2="5" y2="12" />
    <polyline points="12,5 5,12 12,19" />
  </svg>
)

const ForwardIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="4" y1="12" x2="19" y2="12" />
    <polyline points="12,5 19,12 12,19" />
  </svg>
)

const ReloadIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
)

const BrowserWindowIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
  </svg>
)

const MinimizeIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    aria-hidden="true"
  >
    <line x1="2" y1="6" x2="10" y2="6" />
  </svg>
)

const MaximizeIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    aria-hidden="true"
  >
    <rect x="2.5" y="2.5" width="7" height="7" />
  </svg>
)

const CloseIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="2.5" y1="2.5" x2="9.5" y2="9.5" />
    <line x1="9.5" y1="2.5" x2="2.5" y2="9.5" />
  </svg>
)

const Root = styled.div`
  app-region: drag;
  [data-tauri-drag-region] {
    app-region: drag;
  }

  display: flex;
  align-items: center;
  width: 100%;
  height: var(--title-bar-height);
  flex-shrink: 0;
  background: var(--appearance-1);
  border-bottom: 1px solid var(--backgroundA-3);
  position: relative;
  z-index: 300;
  -webkit-app-region: drag;
  -webkit-user-select: none;
  user-select: none;
`

const MacTrafficLightsSpacer = styled.div`
  width: 90px;
  flex-shrink: 0;
`

const Divider = styled.div`
  width: 1px;
  height: 16px;
  background: var(--backgroundA-5);
  margin-inline: 6px;
  flex-shrink: 0;
`

const LeftActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  height: 100%;
  padding-left: 8px;
  -webkit-app-region: no-drag;
`

const DragRegion = styled.div`
  flex: 1;
  height: 100%;
`

const RightActions = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  -webkit-app-region: no-drag;
`

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--textA-11);
  cursor: pointer;
  transition:
    background 0.12s ease,
    color 0.12s ease;

  &:hover:not(:disabled) {
    background: var(--backgroundA-3);
    color: var(--text-12);
  }

  &:active:not(:disabled) {
    background: var(--backgroundA-4);
  }

  &:disabled {
    background: var(--backgroundA-1);
    cursor: default;
  }
`

const OpenInBrowserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  margin: 0;
  padding: 0 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--textA-11);
  font-size: 13px;
  cursor: pointer;
  -webkit-app-region: no-drag;
  transition:
    background 0.12s ease,
    color 0.12s ease;

  &:hover {
    background: var(--backgroundA-3);
    color: var(--text-12);
  }

  &:active {
    background: var(--backgroundA-4);
  }
`

const CloseButton = styled(IconButton)`
  width: 46px;
  border-radius: 0;

  &:hover {
    background: #e81123;
    color: var(--text-12);
  }

  &:active {
    background: #b80e1d;
  }
`
